import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { ApiError } from '../../../lib/api';
import type { DuplicateResult, LiveCampaignRow } from '../../../types/metaLiveCampaigns';
import { duplicateCampaignExactly } from '../api';
import { adsManagerCampaignUrl, describeCounts } from '../liveAdSetFlow';

// Duplicate a live campaign EXACTLY, PAUSED. The server forces the paused deep copy; the only
// choices here are optional dates. Nothing is sent until the AM confirms, and the result shows
// what was copied beside the source so a short copy is visible.
export function DuplicateCampaignDialog({
  accountId,
  campaign,
  onClose,
}: {
  accountId: string;
  campaign: LiveCampaignRow;
  onClose: () => void;
}) {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [result, setResult] = useState<DuplicateResult | null>(null);

  const run = useMutation({
    mutationFn: () => duplicateCampaignExactly(accountId, campaign.id, { startDate: startDate || null, endDate: endDate || null }),
    onSuccess: setResult,
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'The copy failed.'),
  });
  const link = result ? adsManagerCampaignUrl(result.adAccountId, result.copiedCampaignId) : null;

  return (
    <Dialog open onOpenChange={(o) => !o && !run.isPending && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Duplicate "{campaign.name}" exactly (paused)?</DialogTitle>
          <DialogDescription>
            Meta copies every ad set, ad and creative into a NEW campaign named "{campaign.name} – Copy". The copy is created paused and the
            source is not changed. Turn the copy on in Ads Manager when it is ready.
          </DialogDescription>
        </DialogHeader>
        {!result && (
          <div className="space-y-3 text-sm">
            <p className="text-xs text-slate-500">Optional. Leave blank to keep the source's schedule.</p>
            <label className="flex items-center gap-2">
              New start
              <input aria-label="New start date" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="rounded-md border border-slate-300 px-2 py-1" />
            </label>
            <label className="flex items-center gap-2">
              New end
              <input aria-label="New end date" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="rounded-md border border-slate-300 px-2 py-1" />
            </label>
            <p className="text-xs text-slate-500">Only an admin or this account's account manager can do this. It is recorded in the audit log.</p>
          </div>
        )}
        {result && (
          <div className="space-y-2 text-sm" data-testid="duplicate-result">
            <p className="font-medium text-green-700">Copy created (paused). Campaign {result.copiedCampaignId ?? 'id not returned'}.</p>
            <dl className="grid grid-cols-[6rem_1fr] gap-x-3 gap-y-1">
              <dt className="text-xs text-slate-500">Source</dt>
              <dd>{describeCounts(result.source)}</dd>
              <dt className="text-xs text-slate-500">Copy</dt>
              <dd>
                {describeCounts(result.copy)}
                {result.copyCountsFrom === 'copy-map' && <span className="text-xs text-slate-500"> (from Zernio's copy map)</span>}
              </dd>
            </dl>
            {result.short && (
              <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="duplicate-short">
                The copy has fewer ad sets or ads than the source, or could not be checked. Open it in Ads Manager before using it.
              </p>
            )}
            {link && (
              <a className="text-sm font-medium text-kyck-700 underline" href={link} target="_blank" rel="noopener noreferrer">
                View the copy in Ads Manager
              </a>
            )}
          </div>
        )}
        <DialogFooter>
          {!result ? (
            <>
              <Button variant="outline" onClick={onClose} disabled={run.isPending}>
                Cancel
              </Button>
              <Button onClick={() => run.mutate()} disabled={run.isPending}>
                {run.isPending ? 'Copying…' : 'Duplicate (paused)'}
              </Button>
            </>
          ) : (
            <Button onClick={onClose}>Close</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
